import DOMPurify from 'dompurify';

// Sanitisers for the HTML we hand to dangerouslySetInnerHTML.
//
// Two call sites with very different trust levels, so there are two functions
// rather than one shared default:
//
//   sanitizeRichText  — WooCommerce product descriptions. Authored by store
//                       admins, so formatting and links are expected.
//   sanitizeUserText  — customer-submitted review bodies. Untrusted. This is the
//                       stored-XSS path: a review is saved once and then rendered
//                       for every visitor, so one malicious submission runs in
//                       every subsequent reader's browser.
//
// A single permissive sanitiser covering both would mean the review path inherits
// whatever the description path needs, which is how the stricter case quietly
// loses. Keeping them separate makes each allowlist answerable for itself.

// Product descriptions. WooCommerce's editor emits block markup, so the list is
// deliberately broad on *formatting* while still dropping anything executable —
// no <script>, no <iframe>, no event handlers, no <style>.
const RICH_TEXT_CONFIG = {
    ALLOWED_TAGS: [
        'p', 'br', 'hr', 'div', 'span',
        'strong', 'b', 'em', 'i', 'u', 's', 'sub', 'sup', 'small', 'mark',
        'ul', 'ol', 'li', 'dl', 'dt', 'dd',
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'colgroup', 'col',
        'a', 'img', 'figure', 'figcaption',
        'blockquote', 'pre', 'code',
    ],
    ALLOWED_ATTR: ['href', 'title', 'target', 'rel', 'src', 'alt', 'width', 'height', 'colspan', 'rowspan'],
    // No custom ALLOWED_URI_REGEXP on purpose. DOMPurify's default `IS_ALLOWED_URI`
    // already rejects javascript:, data:text/html and vbscript: — verified identical
    // output against a hand-written allowlist regexp. The hand-written one was
    // strictly worse: it added no protection and silently stripped `target` from
    // links, because DOMPurify applies the regexp more broadly than just href/src.
    // Overriding a well-tested default here would be a regression dressed as rigour.

    // Keep the text of a disallowed tag, drop only the tag — losing a description's
    // wording because of one stray element would be a worse bug than the markup.
    KEEP_CONTENT: true,
};

// Review bodies. Customers are writing prose, not laying out a page, so this
// allows only inline emphasis and paragraph breaks. No links: a review is a
// perfect place to plant one, and nothing in the UI needs them.
const USER_TEXT_CONFIG = {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li'],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true,
};

// Force target="_blank" links to carry rel="noopener noreferrer" so sanitised
// markup cannot hand the opener window to a third-party page (same reasoning as
// the hand-written links in Footer.jsx).
let hookInstalled = false;
const installTargetBlankHook = () => {
    if (hookInstalled || typeof DOMPurify.addHook !== 'function') return;
    DOMPurify.addHook('afterSanitizeAttributes', (node) => {
        if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
            node.setAttribute('rel', 'noopener noreferrer');
        }
    });
    hookInstalled = true;
};

const run = (html, config) => {
    // DOMPurify needs a DOM. Returning '' rather than the raw input matters: if this
    // ever runs somewhere without `window`, falling back to unsanitised HTML would
    // defeat the entire point of calling this function.
    if (typeof window === 'undefined') return '';
    if (html === null || html === undefined) return '';
    installTargetBlankHook();
    return DOMPurify.sanitize(String(html), config);
};

export const sanitizeRichText = (html) => run(html, RICH_TEXT_CONFIG);

export const sanitizeUserText = (html) => run(html, USER_TEXT_CONFIG);
